import { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { ScheduleTag } from "@/components/ScheduleTag";

function PickerRow({ item, onAdd }) {
  const options = item.variants?.length
    ? item.variants.map((v) => ({ label: v.name, price: v.price, variant: v.name }))
    : [{ label: "Add", price: item.price, variant: null }];
  return (
    <div className="flex items-center gap-2 py-2 px-2 border-b last:border-0" data-testid={`picker-item-${item.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{item.name}</div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <ScheduleTag value={item.delivery_schedule} />
          {item.week_number ? <span className="text-[10px] text-blue-600">W{item.week_number}</span> : null}
          {!item.available && <span className="text-[10px] text-amber-700 bg-amber-100 px-1.5 rounded">not at this site</span>}
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-1">
        {options.map((o) => (
          <button
            key={o.label}
            type="button"
            onClick={() => onAdd(item, o)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-[#1aa59a]/40 text-[#1aa59a] hover:bg-[#1aa59a] hover:text-white transition-colors"
            data-testid={`picker-add-${item.id}${o.variant ? `-${o.variant}` : ""}`}
          >
            <Plus className="w-3 h-3" />
            {o.variant ? `${o.label} ` : ""}£{Number(o.price).toFixed(2)}
          </button>
        ))}
      </div>
    </div>
  );
}

export function OrderItemPicker({ items, onAdd }) {
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const visible = useMemo(
    () =>
      items.filter(
        (it) => (showAll || it.available) && it.name.toLowerCase().includes(q.trim().toLowerCase())
      ),
    [items, q, showAll]
  );

  return (
    <div className="border rounded-lg" data-testid="order-item-picker">
      <div className="flex items-center gap-2 p-2 border-b bg-gray-50">
        <div className="relative flex-1">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dishes…"
            className="h-8 pl-7 text-sm"
            data-testid="picker-search"
          />
        </div>
        <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap">
          <Switch checked={showAll} onCheckedChange={setShowAll} data-testid="picker-show-all" />
          Show all dishes
        </label>
      </div>
      <div className="max-h-56 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="text-xs text-gray-500 p-4 text-center" data-testid="picker-empty">
            {showAll ? "No dishes match." : "No dishes switched on for this site — try “Show all dishes”."}
          </p>
        ) : (
          visible.map((it) => <PickerRow key={it.id} item={it} onAdd={onAdd} />)
        )}
      </div>
    </div>
  );
}
